import React, { useState, useEffect } from 'react';
import { Meal, Symptom, MealType, CheckinType } from '../lib/types';
import { MealForm } from './MealForm';
import { SymptomForm } from './SymptomForm';
import { X, Utensils, Activity } from 'lucide-react';
import { Sheet } from './Sheet';

interface QuickLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddMeal: (meal: Omit<Meal, 'id'>) => Promise<unknown>;
  onUpdateMeal?: (meal: Meal) => Promise<unknown>;
  onDeleteMeal?: (id: string) => Promise<unknown>;
  onAddSymptom: (symptom: Omit<Symptom, 'id'>) => Promise<unknown>;
  onUpdateSymptom?: (symptom: Symptom) => Promise<unknown>;
  onDeleteSymptom?: (id: string) => Promise<unknown>;
  recentMeals: Meal[];
  initialTab?: 'meal' | 'symptom';
  defaultMealType?: MealType;
  defaultCheckinType?: CheckinType;
  editingMeal?: Meal | null;
  editingSymptom?: Symptom | null;
}

export const QuickLogModal: React.FC<QuickLogModalProps> = ({
  isOpen,
  onClose,
  onAddMeal,
  onUpdateMeal,
  onDeleteMeal,
  onAddSymptom,
  onUpdateSymptom,
  onDeleteSymptom,
  recentMeals,
  initialTab = 'meal',
  defaultMealType,
  defaultCheckinType,
  editingMeal,
  editingSymptom,
}) => {
  const [activeTab, setActiveTab] = useState<'meal' | 'symptom'>(
    editingSymptom ? 'symptom' : editingMeal ? 'meal' : initialTab
  );

  useEffect(() => {
    if (editingSymptom) {
      setActiveTab('symptom');
    } else if (editingMeal) {
      setActiveTab('meal');
    } else {
      setActiveTab(initialTab);
    }
  }, [initialTab, isOpen, editingMeal, editingSymptom]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <Sheet isOpen={isOpen} onClose={onClose} ariaLabel="Log entry">

        {/* Modal Header */}
        <div className="px-6 py-2.5 flex items-center justify-between border-b border-slate-200/60 dark:border-zinc-800/80">
          <div className="flex items-center space-x-2">
            {activeTab === 'meal' ? (
              <Utensils className="w-4 h-4 text-slate-900 dark:text-white" />
            ) : (
              <Activity className="w-4 h-4 text-slate-900 dark:text-white" />
            )}
            <h2 className="text-base font-black uppercase tracking-tight">
              {editingMeal ? (editingMeal.id ? 'Edit Meal Entry' : 'Review Meal') : editingSymptom ? 'Edit Check-In' : activeTab === 'meal' ? 'Log Meal' : 'Log Symptoms'}
            </h2>
          </div>

          <div className="flex items-center space-x-1.5">
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-slate-200/70 dark:bg-zinc-800 flex items-center justify-center text-slate-700 dark:text-zinc-300 hover:text-rose-500 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Scrollable Form Body */}
        <div className="px-6 py-4 overflow-y-auto overscroll-contain overflow-x-hidden flex-1 min-h-0 w-full max-w-full box-border">
          {activeTab === 'meal' ? (
            <MealForm
              initialMeal={editingMeal}
              defaultType={defaultMealType}
              recentMeals={recentMeals}
              onSuccess={async (meal) => {
                await onAddMeal(meal);
                onClose();
              }}
              onUpdate={async (meal) => {
                if (onUpdateMeal) await onUpdateMeal(meal);
                onClose();
              }}
              onDelete={async (id) => {
                if (onDeleteMeal) await onDeleteMeal(id);
                onClose();
              }}
            />
          ) : (
            <SymptomForm
              initialSymptom={editingSymptom}
              recentMeals={recentMeals}
              defaultCheckinType={defaultCheckinType}
              onSuccess={async (symptom) => {
                await onAddSymptom(symptom);
                onClose();
              }}
              onUpdate={async (symptom) => {
                if (onUpdateSymptom) await onUpdateSymptom(symptom);
                onClose();
              }}
              onDelete={async (id) => {
                if (onDeleteSymptom) await onDeleteSymptom(id);
                onClose();
              }}
            />
          )}
        </div>
    </Sheet>
  );
};
